import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as marketingApi from '@/services/marketing.api.js';

// Users with tokens
export function useUsersWithTokens() {
  return useQuery({
    queryKey: ['marketing', 'users-with-tokens'],
    queryFn: () => marketingApi.getUsersWithTokens(),
  });
}

// Test history
export function useTestHistory() {
  return useQuery({
    queryKey: ['marketing', 'test-history'],
    queryFn: () => marketingApi.getTestHistory(),
  });
}

// Send test
export function useSendTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: marketingApi.sendTestNotification,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'test-history'] });
    },
  });
}

// Campaigns list
export function useCampaigns(params: { page?: number; perPage?: number; status?: string; isTest?: boolean }) {
  return useQuery({
    queryKey: ['marketing', 'campaigns', params],
    queryFn: () => marketingApi.listCampaigns(params),
  });
}

// Single campaign
export function useCampaign(id: string | null) {
  return useQuery({
    queryKey: ['marketing', 'campaign', id],
    queryFn: () => marketingApi.getCampaign(id!),
    enabled: !!id,
  });
}

// Create campaign
export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: marketingApi.createCampaign,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'campaigns'] });
      qc.invalidateQueries({ queryKey: ['marketing', 'queue'] });
    },
  });
}

// Cancel campaign
export function useCancelCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: marketingApi.cancelCampaign,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'campaigns'] });
      qc.invalidateQueries({ queryKey: ['marketing', 'queue'] });
    },
  });
}

// Send now
export function useSendCampaignNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: marketingApi.sendCampaignNow,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'campaigns'] });
      qc.invalidateQueries({ queryKey: ['marketing', 'queue'] });
    },
  });
}

// Queue
export function useQueue() {
  return useQuery({
    queryKey: ['marketing', 'queue'],
    queryFn: () => marketingApi.getQueue(),
  });
}

// Templates
export function useTemplates() {
  return useQuery({
    queryKey: ['marketing', 'templates'],
    queryFn: () => marketingApi.listTemplates(),
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: marketingApi.createTemplate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'templates'] });
    },
  });
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      marketingApi.updateTemplate(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'templates'] });
    },
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: marketingApi.deleteTemplate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'templates'] });
    },
  });
}
